import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { apiClient } from '../../../services/api';
import { Table, TableColumn, Submission } from '../../../types/domain';
import { getGridSettings } from '../../../utils/gridPersistence';
import { useTableGridQuery } from './useTableGridQuery';

export type WorkspaceTab =
  | 'data'
  | 'files'
  | 'schema'
  | 'endpoints'
  | 'rules'
  | 'webhooks'
  | 'spam'
  | 'analytics'
  | 'settings';

export const validWorkspaceTabs: WorkspaceTab[] = [
  'data',
  'files',
  'schema',
  'endpoints',
  'rules',
  'webhooks',
  'spam',
  'analytics',
  'settings',
];

export function useTableViewState() {
  const {
    orgId: paramOrgId,
    projectId: paramProjectId,
    tableId: paramTableId,
    tab: paramTab,
  } = useParams<{
    orgId?: string;
    projectId?: string;
    tableId?: string;
    tab?: string;
  }>();

  const navigate = useNavigate();
  const {
    currentOrg,
    currentProject,
    tables,
    currentTable,
    setCurrentTable,
    isTablesLoading,
    permissionTier,
    canEdit,
    canAdmin,
    user,
  } = useAuth();

  const isFullTier = Boolean(canAdmin || user?.is_superadmin);
  const orgId = paramOrgId || currentOrg?.id;
  const projectId = paramProjectId || currentProject?.id;

  const initialTab: WorkspaceTab =
    paramTab && validWorkspaceTabs.includes(paramTab as WorkspaceTab)
      ? (paramTab as WorkspaceTab)
      : 'data';

  const initialTargetTableId = paramTableId || currentTable?.id || (tables && tables[0]?.id);

  const [headerActionEl, setHeaderActionEl] = useState<HTMLDivElement | null>(null);
  const [activeTab, setActiveTab] = useState<WorkspaceTab>(initialTab);
  const [settingsSubTab, setSettingsSubTab] = useState<'general' | 'danger'>('general');
  const [activeTable, setActiveTable] = useState<Table | null>(() => {
    if (paramTableId) {
      return tables.find((t) => t.id === paramTableId) || null;
    }
    return currentTable || (tables && tables.length > 0 ? tables[0] : null);
  });

  const activeTableId = activeTable?.id;

  // Grid Query State extracted
  const gridQuery = useTableGridQuery(initialTargetTableId);

  // Synchronize activeTab when URL paramTab changes
  useEffect(() => {
    if (paramTab === 'notifications') {
      setActiveTab('rules');
    } else if (paramTab && validWorkspaceTabs.includes(paramTab as WorkspaceTab)) {
      setActiveTab(paramTab as WorkspaceTab);
    } else if (!paramTab) {
      setActiveTab('data');
    }
  }, [paramTab]);

  const handleTabChange = useCallback(
    (newTab: WorkspaceTab) => {
      setActiveTab(newTab);
      const targetTableId = paramTableId || activeTable?.id || tables[0]?.id;
      if (!targetTableId) return;

      const baseRoute =
        orgId && projectId
          ? `/organization/${orgId}/projects/${projectId}/tables/${targetTableId}`
          : `/tables/${targetTableId}`;

      const newPath = newTab === 'data' ? baseRoute : `${baseRoute}/${newTab}`;
      navigate(newPath);
    },
    [paramTableId, activeTable?.id, tables, orgId, projectId, navigate]
  );

  const [columns, setColumns] = useState<TableColumn[]>([]);
  const [rows, setRows] = useState<Submission[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Synchronize active table from route params
  const prevTableIdRef = useRef<string | undefined>(initialTargetTableId);
  useEffect(() => {
    if (!currentProject) return;

    let targetTable: Table | null = null;
    if (paramTableId) {
      targetTable = tables.find((t) => t.id === paramTableId) || null;
      if (!targetTable && (isTablesLoading || tables.length === 0)) {
        return;
      }
    }

    if (!targetTable) {
      targetTable = currentTable || tables[0] || null;
      if (targetTable && paramTableId !== targetTable.id) {
        const baseRoute =
          orgId && projectId
            ? `/organization/${orgId}/projects/${projectId}/tables/${targetTable.id}`
            : `/tables/${targetTable.id}`;
        const tabPart = paramTab && paramTab !== 'data' ? `/${paramTab}` : '';
        navigate(`${baseRoute}${tabPart}`, { replace: true });
      }
    }

    if (targetTable) {
      if (targetTable.id !== activeTable?.id) {
        setActiveTable(targetTable);
        setCurrentTable(targetTable);
      }

      if (targetTable.id !== prevTableIdRef.current) {
        prevTableIdRef.current = targetTable.id;
        setRows([]);
        setTotalRows(0);
        gridQuery.setPage(1);

        const saved = getGridSettings(`table_${targetTable.id}`);
        if (saved) {
          gridQuery.setSortCol(saved.sortCol || 'created_at');
          gridQuery.setSortDir(saved.sortDir || 'desc');
          gridQuery.setSortRules(
            saved.sortRules ||
              (saved.sortCol
                ? [{ id: saved.sortCol, desc: saved.sortDir === 'desc' }]
                : [{ id: 'created_at', desc: true }])
          );
          gridQuery.setFilterRules(saved.filterRules || []);
        } else {
          gridQuery.setSortCol('created_at');
          gridQuery.setSortDir('desc');
          gridQuery.setSortRules([{ id: 'created_at', desc: true }]);
          gridQuery.setFilterRules([]);
        }
      }
    }
  }, [
    paramTableId,
    currentProject,
    tables,
    currentTable,
    activeTable?.id,
    navigate,
    setCurrentTable,
    orgId,
    projectId,
    paramTab,
    isTablesLoading,
  ]);

  // Fetch table metadata & columns
  const fetchTableMetadata = useCallback(async () => {
    if (!projectId || !activeTableId) return;
    try {
      const tableRes = await apiClient<{ table: Table; columns: TableColumn[] }>(
        `/projects/${projectId}/tables/${activeTableId}`
      );
      if (tableRes.columns) {
        setColumns(tableRes.columns);
      }
    } catch (err) {
      console.warn('Could not fetch table metadata:', err);
    }
  }, [projectId, activeTableId]);

  useEffect(() => {
    if (activeTableId && projectId) {
      fetchTableMetadata();
    }
  }, [fetchTableMetadata, activeTableId, projectId]);

  const fetchRows = useCallback(
    async (isSilent = false, isManual = false) => {
      if (!projectId || !activeTableId || activeTab !== 'data') {
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }

      if (isManual) {
        setIsRefreshing(true);
      } else if (!isSilent) {
        setIsLoading(true);
      }

      try {
        let sortQuery = '';
        if (gridQuery.sortRules && gridQuery.sortRules.length > 0) {
          sortQuery = gridQuery.sortRules
            .map((s) => `${s.id.replace(/^data_/, '')}:${s.desc ? 'desc' : 'asc'}`)
            .join(',');
        } else if (gridQuery.sortCol) {
          sortQuery = `${gridQuery.sortCol}:${gridQuery.sortDir}`;
        }

        let url = `/projects/${projectId}/tables/${activeTableId}/rows?page=${gridQuery.page}&limit=${gridQuery.pageSize}&sort=${encodeURIComponent(
          sortQuery
        )}&dir=${gridQuery.sortDir}`;
        if (gridQuery.serverSearch) {
          url += `&search=${encodeURIComponent(gridQuery.serverSearch)}`;
        }
        if (gridQuery.serverIdFilter) {
          url += `&id=${encodeURIComponent(gridQuery.serverIdFilter)}`;
        }
        if (gridQuery.filterRules && gridQuery.filterRules.length > 0) {
          url += `&filters=${encodeURIComponent(JSON.stringify(gridQuery.filterRules))}`;
        }

        const rowsRes = await apiClient<{
          data: Submission[];
          columns?: TableColumn[];
          total: number;
          page: number;
          limit: number;
        }>(url);

        const nextRows = rowsRes.data || [];
        setRows((prev) => {
          if (prev.length === nextRows.length) {
            let unchanged = true;
            for (let i = 0; i < prev.length; i++) {
              if (
                prev[i].id !== nextRows[i].id ||
                prev[i].updated_at !== nextRows[i].updated_at ||
                prev[i].is_spam !== nextRows[i].is_spam ||
                prev[i].status !== nextRows[i].status
              ) {
                unchanged = false;
                break;
              }
            }
            if (unchanged) return prev;
          }
          return nextRows;
        });

        setTotalRows((prev) => (prev === (rowsRes.total || 0) ? prev : rowsRes.total || 0));
        if (gridQuery.filterRules.length === 0 && !gridQuery.serverSearch && !gridQuery.serverIdFilter) {
          gridQuery.setUnfilteredTotal((prev) => (prev === (rowsRes.total || 0) ? prev : rowsRes.total || 0));
        }
        if (rowsRes.columns && rowsRes.columns.length > 0) {
          setColumns((prev) => {
            const next = rowsRes.columns!;
            if (
              prev.length === next.length &&
              prev.every(
                (c, i) =>
                  c.id === next[i].id &&
                  c.key === next[i].key &&
                  c.name === next[i].name &&
                  c.data_type === next[i].data_type &&
                  c.width === next[i].width
              )
            ) {
              return prev;
            }
            return next;
          });
        }
      } catch (err) {
        console.warn('Could not fetch rows from API, using empty dataset:', err);
        if (!isSilent) {
          setRows([]);
          setTotalRows(0);
        }
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [
      activeTab,
      projectId,
      activeTableId,
      gridQuery.page,
      gridQuery.pageSize,
      gridQuery.serverSearch,
      gridQuery.serverIdFilter,
      gridQuery.sortCol,
      gridQuery.sortDir,
      gridQuery.sortRules,
      gridQuery.filterRules,
    ]
  );

  // Auto-fetch rows on data tab
  useEffect(() => {
    if (activeTab === 'data' && activeTableId && projectId) {
      fetchRows(rows.length > 0, false);
    } else if (activeTab !== 'data') {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [fetchRows, activeTableId, projectId, activeTab]);

  // Window focus re-fetch
  useEffect(() => {
    const handleFocus = () => {
      if (activeTab === 'data' && activeTableId && projectId && !gridQuery.isEditingRef.current) {
        fetchRows(true, false);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [activeTab, activeTableId, projectId, fetchRows, gridQuery.isEditingRef]);

  // Background polling (every 4s)
  useEffect(() => {
    if (activeTab !== 'data' || !activeTableId || !projectId) return;
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (!gridQuery.isEditingRef.current) {
        fetchRows(true, false);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [activeTab, activeTableId, projectId, fetchRows, gridQuery.isEditingRef]);

  return {
    currentProject,
    tables,
    activeTable,
    setActiveTable,
    setCurrentTable,
    isTablesLoading,
    permissionTier,
    canEdit,
    isFullTier,
    headerActionEl,
    setHeaderActionEl,
    activeTab,
    handleTabChange,
    settingsSubTab,
    setSettingsSubTab,
    columns,
    setColumns,
    rows,
    totalRows,
    page: gridQuery.page,
    setPage: gridQuery.setPage,
    pageSize: gridQuery.pageSize,
    handlePageSizeChange: gridQuery.handlePageSizeChange,
    isLoading,
    isRefreshing,
    filterRules: gridQuery.filterRules,
    handleFilterChange: gridQuery.handleFilterChange,
    unfilteredTotal: gridQuery.unfilteredTotal,
    sortCol: gridQuery.sortCol,
    sortDir: gridQuery.sortDir,
    sortRules: gridQuery.sortRules,
    handleSortChange: gridQuery.handleSortChange,
    handleEditingChange: gridQuery.handleEditingChange,
    handleServerSearchChange: gridQuery.handleServerSearchChange,
    fetchRows,
    resolvedTable: activeTable || tables[0],
    paramTableId,
  };
}
